import React, { useState, useCallback } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import Accordion from '@material-ui/core/Accordion';
import AccordionSummary from '@material-ui/core/AccordionSummary';
import AccordionDetails from '@material-ui/core/AccordionDetails';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import CollectEvidence from 'components/Forms/Fields/CollectEvidence/collectEvidence';
import { evidenceCollectionColumns } from 'containers/Pages/Tasks/Generic/OperationalComponents/helpers';
import { fromJSON } from 'utils/helper/baseTasks';
import { isJSON } from 'utils/checkImage';
import isEmpty from 'lodash/isEmpty';
import PopUp from 'components/Operational/PopUp/PopUp';
import Checkbox from 'components/Checkbox';
import { PATROL_TYPE } from 'utils/constants/patrol';
import { popupText } from 'utils/constants/popupText';
import { TASK_EVIDENCE_TYPE } from 'utils/constants/task';
import PropTypes from 'prop-types';

const useStyles = makeStyles({
  root: {
    width: '100%',
  },
  accordionRoot: {
    display: 'block !important',
  },
});

const formatContents = contents => {
  if (contents === null) {
    return null;
  }
  // check if the content is json string
  const isContentJSON = isJSON(contents);
  if (isContentJSON) {
    return fromJSON(contents);
  }
  return contents;
};
// this format is required to reuse the CollectEvidence component
const evidenceTransform = ({ evidence, linkedTemplateTaskId }) => {
  if (!evidence) return [];
  return evidence.map(ev => {
    return {
      rowData: {
        type: ev.type,
        description: ev.description,
        contents: {
          type: ev.type,
          value:
            ev.type === TASK_EVIDENCE_TYPE.SIGNATURE.id ||
            ev.type === TASK_EVIDENCE_TYPE.OPTION_MULTIPLE.id ||
            ev.type === TASK_EVIDENCE_TYPE.OPTION_SINGLE.id
              ? formatContents(ev.contents)
              : ev.contents, // make sure this is always in this form primitives like string, object or array and not json
        },
      },
      allowEdit: false,
      allowDelete: false,
      rawData: { ...ev, linkedTemplateTaskId },
    };
  });
};
const checkIfContentNeedsFormating = type => {
  return (
    type === TASK_EVIDENCE_TYPE.OPTION_MULTIPLE.id ||
    type === TASK_EVIDENCE_TYPE.OPTION_SINGLE.id
  );
};
const setCompletedContent = ev => {
  if (ev.type === TASK_EVIDENCE_TYPE.CHECKBOX.id) {
    return { type: ev.type, value: [] };
  }
  if (checkIfContentNeedsFormating(ev.type)) {
    return { type: ev.type, value: formatContents(ev.contents) };
  }
  return { type: ev.type, value: ev.contents };
};

const PatrolPathWithEvidenceCollection = ({
  checkpointDetails = [],
  uploadPatrolTask,
}) => {
  const checkIfFirstPointisPatrolled = checkpointDetails.some(
    f => f.text === 1 && f.patrolled,
  );
  /* eslint-disable no-param-reassign */
  const formatCheckpointContent = checkpointDetails => {
    return checkpointDetails.reduce((prev, curr) => {
      if (curr.type === 'WAYPOINT') {
        return prev;
      }
      if (!curr.taskId) return prev; // no evidence attached
      prev = {
        ...prev,
        [curr.taskId]: curr?.evidence
          ? curr.evidence.map(ev => {
              if (ev.completed) {
                return {
                  ...ev,
                  completed: { value: true },
                  contents: setCompletedContent(ev),
                };
              }
              return {
                ...ev,
                completed: { value: false },
                contents: {
                  type: ev.type,
                  value:
                    ev.type === TASK_EVIDENCE_TYPE.SIGNATURE.id ||
                    ev.type === TASK_EVIDENCE_TYPE.OPTION_MULTIPLE.id ||
                    ev.type === TASK_EVIDENCE_TYPE.OPTION_SINGLE.id
                      ? formatContents(ev.contents)
                      : ev.contents,
                },
              };
            })
          : [],
      };
      return prev;
    }, {});
  };

  const [content, setContent] = useState(
    formatCheckpointContent(checkpointDetails),
  );

  const [showForm, setShowForm] = useState(false);
  const [
    currentCheckpointDataInPopup,
    setCurrentCheckpointDataInPopup,
  ] = useState({});
  const [accordionExpand, setExpand] = useState();
  const classes = useStyles();

  const checkIfAllEvidenceAdded = ({
    evidence,
    linkedTemplateTaskId,
    checkPointSeqNumber,
  }) => {
    if (!evidence) {
      // no evidence attached
      uploadPatrolTask({
        currentPointEvidence: [],
        linkedTemplateTaskId,
        checkPointSeqNumber,
        listOfRequiredEvidence: content,
      });
      return;
    }

    // check if checkpoint details has evidence linked to it
    if (!isEmpty(content)) {
      // when you press delete button it will remove the evidence from content
      const evidenceExists = isEmpty(content[linkedTemplateTaskId]);
      if (evidenceExists) {
        setShowForm(prev => !prev);
        return;
      }
      const evidenceNotCompleted = content[linkedTemplateTaskId].find(
        f => !f.completed.value,
      );

      if (evidenceNotCompleted) {
        // if any of the evidence are not filled show a form
        setShowForm(prev => !prev);
        setCurrentCheckpointDataInPopup({
          currentPointEvidence: content[linkedTemplateTaskId],
          linkedTemplateTaskId,
          checkPointSeqNumber,
          listOfRequiredEvidence: content,
        });
        return;
      } else {
        // all the evidence are collected
        uploadPatrolTask({
          currentPointEvidence: content[linkedTemplateTaskId],
          linkedTemplateTaskId,
          checkPointSeqNumber,
          listOfRequiredEvidence: content,
        });
        return;
      }
    }
    setShowForm(prev => !prev);
  };

  const setFieldValue = (field, val) => {
    // make a map of checkpoint evidence with the taskID it is associated with
    setContent(prev => ({
      ...prev,
      [field]: val,
    }));
  };

  const setCompleteToFalse = (field, val) => {
    if (content[field]) {
      setContent(prev => {
        return {
          ...prev,
          [field]: content[field].map(m => {
            if (m.id === val.id) {
              return {
                ...m,
                completed: {
                  value: val.completed,
                },
                contents: {
                  ...m.contents,
                  value: checkIfContentNeedsFormating(m.type)
                    ? formatContents(val.contents)
                    : null,
                },
              };
            }
            return m;
          }),
        };
      });
    }
  };
  const renderCheckpointEvidences = ({
    evidence,
    linkedTemplateTaskId,
    disabled,
  }) => {
    return (
      <AccordionDetails classes={{ root: classes.accordionRoot }}>
        <CollectEvidence
          data={evidenceTransform({ evidence, linkedTemplateTaskId })}
          columns={evidenceCollectionColumns}
          disabled={disabled}
          removeStartEndButtons={true}
          setFieldValue={setFieldValue}
          setCompleteToFalse={setCompleteToFalse}
          field={linkedTemplateTaskId}
          value={
            !isEmpty(content[linkedTemplateTaskId])
              ? content[linkedTemplateTaskId]
              : []
          }
        />
      </AccordionDetails>
    );
  };

  const shouldPatrolPointBeDisabled = useCallback(
    marker => {
      return marker.text === 1
        ? marker.patrolled
          ? marker.patrolled
          : false
        : marker.patrolled
        ? marker.patrolled
        : !checkIfFirstPointisPatrolled;
    },
    [checkIfFirstPointisPatrolled],
  );

  const handleAccordionExpand = text => (_, newExpanded) => {
    setExpand(newExpanded ? text : false);
  };

  return (
    <div className={classes.root}>
      {checkpointDetails.map((marker, key) => (
        <Accordion
          expanded={
            accordionExpand === marker.text ||
            (!marker.patrolled && marker.evidence)
          }
          key={key}
          onChange={handleAccordionExpand(marker.text)}
        >
          <AccordionSummary
            expandIcon={
              marker.type === PATROL_TYPE.CHECKPOINT.id &&
              marker.evidence && <ExpandMoreIcon />
            }
            aria-controls={`${marker.text}-content`}
            id={`${marker.text}-header`}
          >
            <FormControlLabel
              aria-label="Acknowledge"
              onClick={event => event.stopPropagation()}
              onFocus={event => event.stopPropagation()}
              control={
                marker.type === PATROL_TYPE.CHECKPOINT.id ? (
                  <Checkbox
                    disabled={shouldPatrolPointBeDisabled(marker)}
                    onChange={() =>
                      checkIfAllEvidenceAdded({
                        evidence: marker.evidence,
                        linkedTemplateTaskId: marker.taskId,
                        checkPointSeqNumber: marker.text,
                      })
                    }
                    value={marker.patrolled}
                  />
                ) : (
                  <Checkbox disabled={true} />
                )
              }
              label={
                marker.type === PATROL_TYPE.CHECKPOINT.id
                  ? `Checkpoint-${marker.text}`
                  : 'Waypoint'
              }
            />
          </AccordionSummary>
          {marker.type === PATROL_TYPE.CHECKPOINT.id &&
            marker.evidence &&
            renderCheckpointEvidences({
              evidence: marker.evidence,
              linkedTemplateTaskId: marker.taskId,
              disabled: shouldPatrolPointBeDisabled(marker),
            })}
        </Accordion>
      ))}
      {showForm && (
        <PopUp
          title={popupText.patrolPath.popupTitle}
          statusMessage={popupText.patrolPath.popupStatusMessage}
          submit={() => {
            setShowForm(prev => !prev);
            uploadPatrolTask({
              ...currentCheckpointDataInPopup,
            });
          }}
          submitBtnText={popupText.patrolPath.submitBtnText}
          cancel={() => setShowForm(prev => !prev)}
          cancelBtnText={popupText.patrolPath.cancelBtnText}
          close={() => {
            setShowForm(prev => !prev);
            setCurrentCheckpointDataInPopup({});
          }}
        />
      )}
    </div>
  );
};
PatrolPathWithEvidenceCollection.propTypes = {
  checkpointDetails: PropTypes.array,
  uploadPatrolTask: PropTypes.func,
};
export default PatrolPathWithEvidenceCollection;
